import { useState } from "react";
import { Calculator, Clock3, DollarSign, PiggyBank, TrendingUp } from "lucide-react";
import { Slider } from "../ui/slider";
import { Reveal, SectionHeading } from "./Reveal";
import { useSite } from "../../context/SiteContext";

const usd = (n) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);
const num = (n) => new Intl.NumberFormat("en-US").format(Math.round(n));

const WEEKS = 48;

const InputRow = ({ label, value, display, min, max, step, onChange, testid }) => (
  <div>
    <div className="flex items-center justify-between mb-3">
      <label className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400">{label}</label>
      <span className="font-display text-lg font-bold text-brand-navy dark:text-brand-sky" data-testid={`${testid}-value`}>{display}</span>
    </div>
    <Slider value={[value]} min={min} max={max} step={step} onValueChange={([v]) => onChange(v)} className="py-3 cursor-pointer" data-testid={testid} />
  </div>
);

const ResultRow = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="flex items-center gap-5 rounded-2xl bg-white/[0.07] border border-white/15 p-5">
    <div className="w-11 h-11 rounded-xl bg-brand-sky/15 border border-brand-sky/25 flex items-center justify-center shrink-0">
      <Icon size={19} className="text-brand-sky" />
    </div>
    <div className="min-w-0">
      <p className="text-[11px] font-bold uppercase tracking-widest text-white/50">{label}</p>
      <p className="font-display text-xl md:text-2xl font-bold text-white tracking-tight" data-testid={testid}>{value}</p>
      {sub && <p className="text-[11px] text-brand-sky/80 font-semibold mt-0.5">{sub}</p>}
    </div>
  </div>
);

const ROICalculator = () => {
  const { openBooking } = useSite();
  const [patients, setPatients] = useState(20);
  const [days, setDays] = useState(5);
  const [reimb, setReimb] = useState(125);
  const [hasScribe, setHasScribe] = useState(false);

  const annualVisits = patients * days * WEEKS;
  const gainLow = annualVisits * 0.10 * reimb;
  const gainHigh = annualVisits * 0.15 * reimb;
  const savingsLow = hasScribe ? 15000 : 0;
  const savingsHigh = hasScribe ? 30000 : 0;
  const hoursLow = 2 * days * WEEKS;
  const hoursHigh = 3 * days * WEEKS;

  return (
    <section id="roi" className="bg-white dark:bg-brand-ink py-24 md:py-32" data-testid="roi-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <SectionHeading
          id="roi"
          overline="ROI Calculator"
          title={<>What could live scribing be <span className="text-brand-gradient">worth to your practice?</span></>}
          sub="Enter your patient volume and see estimated yearly impact — based on the published research ranges cited across this site."
        />
        <div className="mt-14 grid lg:grid-cols-[1fr_1.05fr] gap-8 items-stretch">
          <Reveal>
            <div className="h-full rounded-[2rem] bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-white/10 p-8 md:p-10 space-y-9" data-testid="roi-inputs-card">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-brand-navy dark:bg-brand-sky flex items-center justify-center">
                  <Calculator size={18} className="text-white dark:text-brand-ink" />
                </div>
                <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-white">Your practice</h3>
              </div>
              <InputRow label="Patients per day" value={patients} display={patients} min={5} max={50} step={1} onChange={setPatients} testid="roi-patients-slider" />
              <InputRow label="Clinic days per week" value={days} display={days} min={1} max={6} step={1} onChange={setDays} testid="roi-days-slider" />
              <InputRow label="Avg. reimbursement per visit" value={reimb} display={usd(reimb)} min={50} max={400} step={5} onChange={setReimb} testid="roi-reimbursement-slider" />
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-slate-500 dark:text-slate-400 mb-3">Currently using an in-person scribe?</p>
                <div className="flex gap-2.5">
                  {[["Yes", true], ["No", false]].map(([label, val]) => (
                    <button
                      key={label}
                      data-testid={`roi-scribe-toggle-${label.toLowerCase()}`}
                      onClick={() => setHasScribe(val)}
                      aria-pressed={hasScribe === val}
                      className={`rounded-full px-6 py-2.5 text-xs font-bold transition-colors duration-300 ${
                        hasScribe === val
                          ? "bg-brand-navy text-white dark:bg-brand-sky dark:text-brand-ink"
                          : "border border-slate-200 dark:border-white/10 text-slate-500 dark:text-slate-400 hover:border-brand-sky"
                      }`}
                    >
                      {label}
                    </button>
                  ))}
                </div>
              </div>
              <p className="text-[11px] leading-relaxed text-slate-400">
                Assumes {WEEKS} working weeks/year. Throughput lift of 10–15% and $15K–$30K staffing savings reflect the
                research ranges cited in our Resources — estimates, not a guarantee.
              </p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="relative h-full overflow-hidden rounded-[2rem] bg-gradient-to-br from-brand-navy-deep via-brand-navy to-[#1E4C8F] p-8 md:p-10 grain" data-testid="roi-results-card">
              <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-brand-sky/20 blur-3xl" />
              <div className="relative space-y-4">
                <p className="text-xs font-bold uppercase tracking-[0.25em] text-brand-sky">Estimated yearly impact</p>
                <ResultRow
                  icon={TrendingUp}
                  label="Additional revenue from throughput"
                  value={`${usd(gainLow)} – ${usd(gainHigh)}`}
                  sub={`${num(annualVisits * 0.10)}–${num(annualVisits * 0.15)} extra visits · 10–15% more patients per session`}
                  testid="roi-revenue-gain"
                />
                {hasScribe && (
                  <ResultRow
                    icon={PiggyBank}
                    label="Savings vs. in-person scribe"
                    value={`${usd(savingsLow)} – ${usd(savingsHigh)}`}
                    sub="Wages, benefits, training churn and space costs removed"
                    testid="roi-savings"
                  />
                )}
                <ResultRow
                  icon={Clock3}
                  label="Documentation hours reclaimed"
                  value={`${num(hoursLow)} – ${num(hoursHigh)} hrs`}
                  sub="≈ 2–3 hours of after-visit charting removed per clinic day"
                  testid="roi-hours"
                />
                <div className="rounded-2xl bg-brand-sky/15 border border-brand-sky/30 p-5 flex items-center gap-5">
                  <div className="w-11 h-11 rounded-xl bg-brand-sky flex items-center justify-center shrink-0">
                    <DollarSign size={19} className="text-brand-ink" />
                  </div>
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-widest text-brand-sky">Total estimated annual value</p>
                    <p className="font-display text-2xl md:text-3xl font-bold text-white tracking-tight" data-testid="roi-total">
                      {usd(gainLow + savingsLow)} – {usd(gainHigh + savingsHigh)}
                    </p>
                  </div>
                </div>
                <button
                  data-testid="roi-book-call-button"
                  onClick={openBooking}
                  className="w-full rounded-full bg-white text-brand-navy font-bold px-8 py-4 text-sm hover:shadow-[0_20px_50px_-15px_rgba(255,255,255,0.45)] hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300"
                >
                  See Your Real Numbers — Book a Discovery Call
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
export default ROICalculator;
